/*
 * Die Rasterflaeche des HMI-Editors, Bedienseite: die Flaeche, auf der ein
 * Autor Widgets schiebt, in der Groesse zieht, auswaehlt und loescht.
 *
 * ARBEITSTEILUNG. Das Uebersetzen zwischen Schema und Gridstack und das
 * Mounten liegen in `hmiGridHost.ts`, die Regeln auf dem Projekt in
 * `editorModel.ts`, die Historie im `editorStore`. Hier steht nur, was
 * zwischen Maus, Tastatur und diesen dreien passiert. Jede Aenderung am
 * Projekt geht ueber `store.apply`, sonst faellt sie aus der
 * Rueckgaengig-Kette und der Autor verliert sie beim naechsten Zurueck.
 *
 * DAS DOM DER ZELLEN GEHOERT GRIDSTACK, DER INHALT UNS. Wie beim Dashboard
 * (`pages/DashboardPage.tsx`) rendern wir die Widgets mit einem eigenen
 * `render()` in die `.grid-stack-item-content` hinein, statt die Zellen von
 * Preact bauen zu lassen. Der Grund ist handfest: Gridstack schreibt
 * waehrend eines Zuges laufend Attribute und Stile auf genau diese
 * Elemente, und ein Preact, das dieselben Knoten besitzt, wuerde ihm dabei
 * ins Lenkrad greifen.
 *
 * DREI QUELLEN FUER DIE ANORDNUNG, EINE WAHRHEIT. Was das Raster zeigt, ist
 * entweder das, was im Projekt steht, oder eine uebernommene Anordnung aus
 * einem anderen Ziel (`store.proposal`), oder fuer die Widgets ohne Eintrag
 * eine Anfangsanordnung aus `autoFlowPlacements`. Die letzten beiden sind
 * Vorschlaege und werden als solche gekennzeichnet; ins Projekt kommen sie
 * erst, wenn der Autor bestaetigt oder die Flaeche anfasst. Dann allerdings
 * vollstaendig: wer ein Widget verschiebt, legt damit auch fest, wo die
 * anderen stehen, die er gerade sieht.
 */

import { render as renderPreact } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { useOptionalPointBindingSource } from '../PointBindingProvider';
import { WidgetHost } from '../WidgetHost';
import { LeafMetaLookupProvider, useLeafMetaLookup } from '../LeafMetaContext';
import { hmiAppearanceAttributes } from '../appearanceModel';
import { ThemeScope } from '../panel/ThemeScope';
import { HmiRuntimeProvider, useHmiRuntime } from '../panel/runtimeContext';
import { WIDGET_KIND_DRAG_TYPE } from './dragTypes';
import type { HmiProject, LayoutTarget, WidgetPlacement } from '../widgets';
import {
  GRID_SPECS,
  LAYOUT_TARGET_ORDER,
  alignPlacements,
  duplicateWidget,
  findScreen,
  matchPlacementSize,
  placementsOf,
  removeWidget,
  setPlacements,
  transferLayout,
} from './editorModel';
import type { EditorStore } from './editorStore';
import {
  autoFlowPlacements,
  cellAtPoint,
  mountHmiGrid,
  signatureOf,
  toGridItems,
  type HmiGridHandle,
} from './hmiGridHost';

// Gridstack bringt seine eigenen Visuals mit. Das zweite Stylesheet ist
// nicht optional: die Basisdatei kennt nur die Spaltenzahlen 1 und 12, und
// das Ziel `mobileResponsive` hat vier. Ohne `gridstack-extra` waere das
// Handy-Layout zwoelfspaltig dargestellt und jede Breite dort gelogen.
import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.min.css';
import '../hmiCell.css';
import './HmiCanvas.css';

/** Weitergereicht, damit Aufrufer den Typ nicht selbst suchen muessen. Er
 *  steht in `dragTypes.ts`; dort steht auch, warum er dort steht.
 *
 *  Der Import daneben ist noetig und keine Dublette: ein `export ... from`
 *  reicht den Namen nach aussen, holt ihn aber NICHT in diesen
 *  Modulgeltungsbereich, und die Ablege-Pruefung weiter unten braucht ihn
 *  hier. */
export { WIDGET_KIND_DRAG_TYPE } from './dragTypes';

interface Props {
  store: EditorStore;
  /**
   * Ein Widget-Kind wurde auf der Flaeche abgelegt.
   *
   * Die Flaeche legt selbst KEINE Widgets an. Das Anlegen kennt die
   * typspezifischen Pflichtfelder (jeder der Widget-Typen hat eigene) und
   * gehoert zur Palette; hier wird nur gemeldet, was wo abgelegt wurde.
   */
  onDropWidget?: (kind: string, at: { col: number; row: number }) => void;
  /**
   * Darf der Autor ueberhaupt aendern?
   *
   * Betrifft nur die Werkzeuge ueber der Flaeche. Das Ziehen selbst haengt
   * nicht daran: wer nicht speichern darf, sieht ohnehin nur einen Entwurf,
   * und eine Flaeche, auf der sich nichts bewegen laesst, waere beim
   * Nachsehen im Weg statt hilfreich.
   */
  canEdit?: boolean;
}

/** Tastenversatz um eine Zelle. */
const ARROW_STEPS: Readonly<Record<string, { col: number; row: number }>> = {
  ArrowLeft: { col: -1, row: 0 },
  ArrowRight: { col: 1, row: 0 },
  ArrowUp: { col: 0, row: -1 },
  ArrowDown: { col: 0, row: 1 },
};

export function HmiCanvas({ store, onDropWidget, canEdit = true }: Props) {
  const project = store.project.value;
  const screenId = store.activeScreenId.value;
  const target = store.activeTarget.value;
  const selection = store.selection.value;

  const spec = GRID_SPECS[target];
  const screen = findScreen(project, screenId);
  const widgetIds = screen?.widgetIds ?? [];
  const stored = placementsOf(project, screenId, target);

  /**
   * Der Uebernahme-Vorschlag, aber nur wenn er zu dem gehoert, was gerade zu
   * sehen ist. Ein Vorschlag fuer ein anderes Bild oder ein anderes Ziel
   * traegt Zahlen, die hier zufaellig aussehen wuerden.
   */
  const rawProposal = store.proposal.value;
  const proposal =
    rawProposal && rawProposal.screenId === screenId && rawProposal.target === target
      ? rawProposal
      : null;

  /**
   * Die Platzierungen, die im PROJEKT stehen und damit gesicherte Arbeit
   * sind. Solange ein Vorschlag angezeigt wird, ist das keine einzige: der
   * Vorschlag ersetzt die Anordnung des Ziels als Ganzes, und ihn mit dem
   * alten Stand zu mischen ergaebe eine Anordnung, die es so nie gab.
   */
  const settled = useMemo(
    () => (proposal ? new Set<string>() : new Set(Object.keys(stored))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, screenId, target, proposal],
  );

  /**
   * Was die Flaeche zeigt: die gesicherte oder vorgeschlagene Anordnung,
   * ergaenzt um eine Anfangsanordnung fuer alles, was danach noch keine hat.
   *
   * Der letzte Fall trifft nicht nur ein unberuehrtes Ziel, sondern auch ein
   * Widget, das auf dem Desktop angelegt und auf dem Panel noch nie
   * angefasst wurde, und die Widgets, die bei einer Uebernahme auf dem
   * Zielraster keinen Platz fanden. Jedes Mal ist die Antwort dieselbe:
   * zeigen, kennzeichnen, nicht speichern.
   */
  const effective = useMemo(() => {
    const out: Record<string, WidgetPlacement> = {};
    const missing: string[] = [];
    for (const id of widgetIds) {
      const placement = proposal ? proposal.placements[id] : stored[id];
      if (placement) out[id] = placement;
      else missing.push(id);
    }
    if (missing.length > 0) {
      const below = Object.values(out).reduce((max, p) => Math.max(max, p.row + p.rowSpan), 0);
      Object.assign(out, autoFlowPlacements(missing, spec, below));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, screenId, target, proposal]);

  const unplaced = widgetIds.filter((id) => !settled.has(id));

  const items = useMemo(() => toGridItems(effective), [effective]);
  // Der Vergleichsschluessel fuer die Nachfuehr-Wirkung. Die Item-Liste ist
  // bei jedem Rendern ein neues Objekt; ohne diesen Schluessel wuerde das
  // Raster auch bei einer reinen Beschriftungsaenderung neu geladen, und ein
  // Neuladen mitten im Zug ruckelt sichtbar.
  const signature = signatureOf(items);

  /**
   * Die Datenquelle, ausdruecklich als Prop und nicht ueber den Context.
   *
   * Der Grund ist die Bauweise dieser Flaeche: die Widgets entstehen in einem
   * EIGENEN Preact-Baum, den `render()` in die Gridstack-Zelle haengt, und
   * ein eigener Baum erbt keinen Context. Ohne diese Zeile zeigt jedes Widget
   * im Editor "Keine Datenquelle", obwohl der Provider eine Ebene darueber
   * steht. Genau so ist es beim ersten Durchlauf am 2026-09-16 passiert; der
   * sichtbare Fallback in `WidgetHost` hat es gefangen, eine leere Kachel
   * haette es verborgen.
   */
  const bindingSource = useOptionalPointBindingSource();
  // Same reason as the binding source: each cell is its own Preact tree, so
  // the leaf metadata (units, enum texts, limits) has to be handed in.
  const leafMetaLookup = useLeafMetaLookup();
  // Same again for the runtime context (screens, navigation, editing): the
  // nav-bar and action-button read it, and a cell tree would get the
  // harmless default instead of the editor's.
  const runtime = useHmiRuntime();

  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HmiGridHandle | null>(null);
  const [ready, setReady] = useState(0);
  /**
   * Die Rasterbibliothek kam nicht. Muss sichtbar sein: ohne sie bleibt die
   * Flaeche leer, und eine leere Flaeche liest sich wie ein leeres Bild. Der
   * Autor wuerde weiterzeichnen und sich wundern, dass nichts erscheint.
   */
  const [loadError, setLoadError] = useState<string | null>(null);

  // Der jeweils aktuelle Stand fuer die Rueckrufe (Tastatur, Gridstack). Die
  // Rueckrufe leben laenger als ein Rendern, und eine eingefangene alte
  // Anordnung wuerde beim naechsten Tastendruck den vorletzten Stand
  // zurueckschreiben.
  const effectiveRef = useRef(effective);
  effectiveRef.current = effective;
  const unplacedRef = useRef(unplaced.length > 0);
  unplacedRef.current = unplaced.length > 0;

  // --------------------------------------------------- Raster auf- und abbauen
  //
  // Abhaengig von Bild und Ziel, und bewusst nicht vom Projekt: ein Raster,
  // das bei jeder Eigenschaftsaenderung neu entstuende, verloere die
  // laufende Geste. Umgekehrt MUSS es beim Wechsel neu entstehen, denn
  // Spaltenzahl und Zeilenhoehe stecken in der Instanz. Das Abraeumen ist
  // hier die eigentliche Arbeit: ein Leck zeigt sich als Widget, das nach dem
  // dritten Wechsel doppelt haengt.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    const handle = mountHmiGrid(surface, spec, items, {
      onLayoutChange: (placements, gestureKey) => {
        const current = store.project.peek();
        store.apply(
          setPlacements(current, screenId, target, placements),
          `raster:${screenId}:${target}:${gestureKey}`,
        );
      },
      onReady: () => setReady((tick) => tick + 1),
      onError: (err) =>
        setLoadError(err instanceof Error ? err.message : String(err)),
    });
    hostRef.current = handle;
    setLoadError(null);

    return () => {
      hostRef.current = null;
      // Erst die Preact-Baeume in den Zellen abhaengen, dann die Zellen
      // wegnehmen. Andersherum liefen die Aufraeumfunktionen der Widgets
      // (Hub-Abos, Timer) gegen Elemente, die es nicht mehr gibt.
      for (const content of Array.from(
        handle.element.querySelectorAll<HTMLElement>('.grid-stack-item-content'),
      )) {
        renderPreact(null, content);
      }
      handle.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenId, target]);

  // ------------------------------------------------------- Anordnung nachfuehren
  //
  // Alles, was das Projekt aendert, ohne dass jemand gezogen hat:
  // Rueckgaengig, Tastatur, Loeschen, ein Widget aus der Palette, eine
  // uebernommene Anordnung.
  useEffect(() => {
    const handle = hostRef.current;
    if (!handle) return;

    // Zellen, die gleich verschwinden, VORHER abhaengen. Gridstack nimmt
    // beim Laden das Element weg, ohne von dem Preact-Baum darin zu wissen;
    // dessen Aufraeumfunktionen liefen dann nie, und ein geloeschtes Widget
    // bliebe mit seinem Hub-Abo im Speicher haengen.
    const keep = new Set(items.map((item) => item.id));
    for (const item of Array.from(
      handle.element.querySelectorAll<HTMLElement>('.grid-stack-item'),
    )) {
      const id = item.getAttribute('gs-id');
      if (id && keep.has(id)) continue;
      const content = item.querySelector<HTMLElement>('.grid-stack-item-content');
      if (content) renderPreact(null, content);
    }

    handle.sync(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, ready]);

  // ------------------------------------------------------------- Inhalte rendern
  //
  // Das echte Widget, nicht ein Platzhalterkasten: der Autor soll sehen, was
  // er baut, und eine graue Kiste mit dem Typnamen zeigt ihm nicht, ob das
  // Manometer in seine Zelle passt.
  useEffect(() => {
    const handle = hostRef.current;
    if (!handle) return;

    const byId = new Map(project.widgets.map((w) => [w.id, w]));
    const chosen = new Set(selection);
    for (const item of Array.from(
      handle.element.querySelectorAll<HTMLElement>('.grid-stack-item'),
    )) {
      const id = item.getAttribute('gs-id');
      if (!id) continue;

      item.classList.toggle('hv-hmi-canvas__item--selected', chosen.has(id));
      // Das fuehrende Widget bekommt eine eigene Marke. Ohne sie waere bei
      // einer Mehrfachauswahl nicht zu sehen, wonach sich Ausrichten und
      // Angleichen richten, und das Ergebnis saehe willkuerlich aus.
      item.classList.toggle('hv-hmi-canvas__item--anchor', selection.length > 1 && id === selection[0]);
      item.classList.toggle('hv-hmi-canvas__item--proposed', !settled.has(id));

      const content = item.querySelector<HTMLElement>('.grid-stack-item-content');
      if (!content) continue;

      const widget = byId.get(id);
      renderPreact(
        widget ? (
          <HmiRuntimeProvider value={runtime}>
            <LeafMetaLookupProvider lookup={leafMetaLookup}>
              <div class="hv-hmi-canvas__widget hv-hmi-cell">
                <WidgetHost widget={widget} source={bindingSource ?? undefined} />
              </div>
            </LeafMetaLookupProvider>
          </HmiRuntimeProvider>
        ) : (
          // Kann vorkommen, wenn ein Zug und ein Loeschen sich ueberholen.
          // Sichtbar statt leer, sonst sieht es aus wie ein Widget, das
          // nichts anzeigt.
          <div class="hv-hmi-canvas__orphan">Unbekanntes Widget: {id}</div>
        ),
        content,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, selection, signature, ready, bindingSource, leafMetaLookup, settled, runtime]);

  // ------------------------------------------------------------------ Tastatur
  //
  // Am Dokument und nicht an der Flaeche, damit Entf auch dann wirkt, wenn
  // der Autor zuletzt einen Knopf in der Werkzeugleiste gedrueckt hat. Der
  // Preis dafuer ist die Abfrage unten: steht der Fokus in einem Eingabefeld,
  // gehoert die Taste dem Feld. Ohne diese Abfrage loescht das Tippen einer
  // Beschriftung mit Entf das Widget, das man gerade beschriftet, und genau
  // das ist der haeufigste Fehler an dieser Stelle.
  useEffect(() => {
    function onKeyDown(ev: KeyboardEvent): void {
      if (isTextEntry(document.activeElement)) return;

      const ids = store.selection.peek();
      if (ids.length === 0) return;
      const current = store.project.peek();

      if (ev.key === 'Escape') {
        store.selection.value = [];
        return;
      }

      if (ev.key === 'Delete') {
        ev.preventDefault();
        // Alle auf einmal und in EINEM Schritt: wer sechs Kacheln gewaehlt
        // und Entf gedrueckt hat, will sie nicht sechsmal einzeln
        // zurueckholen muessen.
        store.apply(ids.reduce((acc, id) => removeWidget(acc, id), current));
        return;
      }

      if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'd' || ev.key === 'D')) {
        // Ohne preventDefault legt der Browser ein Lesezeichen an.
        ev.preventDefault();
        store.apply(
          ids.reduce(
            (acc, id) => duplicateWidget(acc, screenId, target, id),
            materialise(current),
          ),
        );
        return;
      }

      const step = ARROW_STEPS[ev.key];
      if (!step) return;
      const base = effectiveRef.current;

      // Auch hier der komplette Stand und nicht nur die bewegten Widgets: die
      // Flaeche zeigt womoeglich eine Anfangsanordnung, und ein einzeln
      // geschriebenes Placement liesse die Nachbarn im Projekt weiterhin
      // unplatziert.
      const next: Record<string, WidgetPlacement> = { ...base };
      let moved = false;
      for (const id of ids) {
        const from = base[id];
        if (!from) continue;
        next[id] = { ...from, col: from.col + step.col, row: from.row + step.row };
        moved = true;
      }
      if (!moved) return;
      ev.preventDefault();

      store.apply(
        setPlacements(current, screenId, target, next),
        `tastatur:${screenId}:${target}:${ids.join(',')}`,
      );
    }

    /** Die gezeigte Anordnung festschreiben, bevor eine Aktion sie veraendert.
     *  Sonst entstuende ein Layout, in dem nur das Ergebnis der Aktion steht
     *  und die uebrigen Widgets weiterhin ohne Platzierung daneben liegen. */
    function materialise(current: HmiProject): HmiProject {
      if (!unplacedRef.current) return current;
      return setPlacements(current, screenId, target, effectiveRef.current);
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenId, target]);

  // ------------------------------------------------------------------- Auswahl

  function onSurfaceClick(ev: MouseEvent): void {
    const el = ev.target as Element | null;
    const item = el?.closest?.('.grid-stack-item') ?? null;
    const id = item?.getAttribute('gs-id') ?? null;
    const current = store.selection.peek();

    // Klick ins Leere hebt die Auswahl auf. Das ist der Rueckweg aus einer
    // Auswahl, den jeder kennt, ohne ihn gelernt zu haben.
    if (id === null) {
      if (current.length > 0) store.selection.value = [];
      return;
    }

    // Strg erweitert und nimmt wieder heraus. Angehaengt und nicht
    // einsortiert: die Reihenfolge ist die Wahlreihenfolge, und das zuerst
    // gewaehlte Widget bleibt der Bezug fuer Ausrichten und Angleichen.
    if (ev.ctrlKey || ev.metaKey) {
      store.selection.value = current.includes(id)
        ? current.filter((other) => other !== id)
        : [...current, id];
      return;
    }

    if (current.length === 1 && current[0] === id) return;
    store.selection.value = [id];
  }

  // ------------------------------------------------------ Anordnung uebernehmen

  const sources = LAYOUT_TARGET_ORDER.filter((t) => t !== target);
  const [pickedSource, setPickedSource] = useState<LayoutTarget | null>(null);
  const source = pickedSource !== null && pickedSource !== target ? pickedSource : sources[0];
  /** Warum eine Uebernahme nichts ergeben hat. Ohne diese Zeile klickt der
   *  Autor auf "uebernehmen", es passiert nichts, und er haelt den Knopf fuer
   *  kaputt statt die Quelle fuer leer. */
  const [transferNote, setTransferNote] = useState<string | null>(null);

  // Der Hinweis gehoert zu genau einer Flaeche. Beim Wechsel von Bild oder
  // Ziel bezieht er sich auf etwas, das nicht mehr zu sehen ist.
  useEffect(() => setTransferNote(null), [screenId, target]);
  // The picked source belongs to one target. After switching, start again
  // from the default (the largest other target) instead of a stale choice.
  useEffect(() => setPickedSource(null), [target]);

  function onTransfer(): void {
    setTransferNote(null);
    const result = transferLayout(store.project.peek(), screenId, source, target);
    if (Object.keys(result.placements).length === 0 && result.overflow.length === 0) {
      setTransferNote(
        `Auf ${GRID_SPECS[source].label} ist für dieses Bild noch nichts festgelegt, ` +
        'es gibt also nichts zu übernehmen.',
      );
      return;
    }
    store.proposal.value = { screenId, target, from: source, ...result };
  }

  function onAcceptProposal(): void {
    const current = store.proposal.peek();
    if (!current) return;
    store.apply(
      setPlacements(store.project.peek(), current.screenId, current.target, current.placements),
    );
  }

  // ------------------------------------------------- Ausrichten und angleichen

  /**
   * Eine Anordnungsfunktion auf die Auswahl anwenden.
   *
   * Ohne `coalesceKey`, und das ist der Punkt: links ausrichten und danach
   * gleich breit machen sind zwei Entscheidungen. Der Autor soll die zweite
   * zuruecknehmen koennen, ohne die erste mitzuverlieren, und genau das
   * taete ein gemeinsamer Schluessel.
   */
  function applyToSelection(
    fn: (placements: Readonly<Record<string, WidgetPlacement>>, ids: readonly string[])
      => Record<string, WidgetPlacement>,
  ): void {
    const ids = store.selection.peek();
    if (ids.length < 2) return;
    store.apply(
      setPlacements(store.project.peek(), screenId, target, fn(effectiveRef.current, ids)),
    );
  }

  // ------------------------------------------------------------------- Ablegen

  const [dropHover, setDropHover] = useState(false);

  function carriesWidgetKind(ev: DragEvent): boolean {
    return Array.from(ev.dataTransfer?.types ?? []).includes(WIDGET_KIND_DRAG_TYPE);
  }

  function onDragOver(ev: DragEvent): void {
    if (!onDropWidget || !carriesWidgetKind(ev)) return;
    // Ohne preventDefault lehnt der Browser das Ablegen ab, und zwar
    // wortlos: der Zeiger zeigt "verboten" und niemand sagt warum.
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    setDropHover(true);
  }

  function onDrop(ev: DragEvent): void {
    setDropHover(false);
    if (!onDropWidget || !carriesWidgetKind(ev)) return;
    ev.preventDefault();

    const kind = ev.dataTransfer?.getData(WIDGET_KIND_DRAG_TYPE) ?? '';
    if (!kind) return;

    const gridEl = hostRef.current?.element ?? surfaceRef.current;
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    const at = cellAtPoint(
      { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
      { width: rect.width, rowHeightPx: spec.cellHeightPx },
      spec,
    );
    onDropWidget(kind, at);
  }

  // --------------------------------------------------------------- Darstellung

  const surfaceClasses = [
    'hv-hmi-canvas__surface',
    spec.rows !== null ? 'hv-hmi-canvas__surface--fixed' : '',
    dropHover ? 'hv-hmi-canvas__surface--drop' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const multi = selection.length > 1;

  return (
    <div class="hv-hmi-canvas">
      <div class="hv-hmi-canvas__bar">
        <span class="hv-hmi-canvas__target">{spec.label}</span>
        <span class="hv-hmi-canvas__measure">
          {spec.columns} Spalten
          {spec.rows !== null
            ? ` x ${spec.rows} Zeilen (fest)`
            : ', Zeilen wachsen mit'}
        </span>
      </div>

      {canEdit && screen && (
        <div class="hv-hmi-canvas__tools">
          <div class="hv-hmi-canvas__toolGroup">
            {/* Reads as one sentence, "Anordnung von [source] auf [target]
                übernehmen", so the target is named. The button used to repeat
                the source and the target was nowhere in the bar. */}
            <label class="hv-hmi-canvas__toolLabel" for="hv-hmi-canvas-source">
              Anordnung von
            </label>
            <select
              id="hv-hmi-canvas-source"
              class="hv-hmi-canvas__select"
              value={source}
              onChange={(e) =>
                setPickedSource((e.currentTarget as HTMLSelectElement).value as LayoutTarget)}
            >
              {sources.map((t) => (
                <option key={t} value={t}>{GRID_SPECS[t].label}</option>
              ))}
            </select>
            <span class="hv-hmi-canvas__toolLabel">
              auf <strong>{spec.label}</strong>
            </span>
            <button
              type="button"
              class="hv-btn"
              onClick={onTransfer}
              title={`Die Anordnung von ${GRID_SPECS[source].label} auf ${spec.label} umrechnen. `
                + 'Das Ergebnis ist ein Vorschlag und steht erst nach dem Bestätigen im Projekt.'}
            >
              Übernehmen
            </button>
          </div>

          {/* Ausrichten braucht einen Bezug und damit mindestens zwei
              Widgets. Ausgegraut statt versteckt: ein Werkzeug, das je nach
              Auswahl erscheint und verschwindet, laesst die Leiste springen
              und ist beim zweiten Mal nicht wiederzufinden. */}
          <div class="hv-hmi-canvas__toolGroup">
            <span class="hv-hmi-canvas__toolLabel">Ausrichten</span>
            <button type="button" class="hv-btn" disabled={!multi} title="Linke Kanten"
                    onClick={() => applyToSelection((p, ids) => alignPlacements(p, ids, 'left'))}>
              Links
            </button>
            <button type="button" class="hv-btn" disabled={!multi} title="Rechte Kanten"
                    onClick={() => applyToSelection((p, ids) => alignPlacements(p, ids, 'right'))}>
              Rechts
            </button>
            <button type="button" class="hv-btn" disabled={!multi} title="Obere Kanten"
                    onClick={() => applyToSelection((p, ids) => alignPlacements(p, ids, 'top'))}>
              Oben
            </button>
            <button type="button" class="hv-btn" disabled={!multi} title="Untere Kanten"
                    onClick={() => applyToSelection((p, ids) => alignPlacements(p, ids, 'bottom'))}>
              Unten
            </button>
            <button type="button" class="hv-btn" disabled={!multi} title="Gleiche Breite"
                    onClick={() => applyToSelection((p, ids) => matchPlacementSize(p, ids, 'width'))}>
              Gleich breit
            </button>
            <button type="button" class="hv-btn" disabled={!multi} title="Gleiche Höhe"
                    onClick={() => applyToSelection((p, ids) => matchPlacementSize(p, ids, 'height'))}>
              Gleich hoch
            </button>
          </div>

          {multi && (
            <span class="hv-hmi-canvas__toolNote">
              {selection.length} ausgewählt, Bezug ist das zuerst gewählte.
            </span>
          )}
        </div>
      )}

      {transferNote !== null && (
        <p class="hv-hmi-canvas__note hv-hmi-canvas__note--warn">{transferNote}</p>
      )}

      {loadError !== null && (
        <p class="hv-hmi-canvas__note hv-hmi-canvas__note--warn" role="alert">
          Die Rasterbibliothek ließ sich nicht laden, deshalb bleibt die Fläche leer.
          Das Bild selbst ist unverändert. Meldung: {loadError}
        </p>
      )}

      {!screen && (
        <p class="hv-hmi-canvas__note hv-hmi-canvas__note--warn">
          Dieses Bild gibt es nicht (mehr).
        </p>
      )}

      {screen && widgetIds.length === 0 && (
        <p class="hv-hmi-canvas__note">
          Das Bild "{screen.label}" ist leer. Zieh ein Widget aus der Palette auf die Fläche.
        </p>
      )}

      {/* Der Uebernahme-Vorschlag hat Vorrang vor dem Hinweis auf fehlende
          Platzierungen: solange er steht, ist "noch nichts festgelegt" zwar
          wahr, aber nicht das, was der Autor gerade wissen muss. */}
      {screen && proposal && (
        <div class="hv-hmi-canvas__note hv-hmi-canvas__note--proposal hv-hmi-canvas__transfer" role="status">
          <p class="hv-hmi-canvas__noteText">
            Anordnung von {GRID_SPECS[proposal.from].label} übernommen,{' '}
            {proposal.mode === 'scaled'
              ? 'maßstäblich umgerechnet'
              : 'in Lesereihenfolge neu umgebrochen, weil sie sich nicht ohne Überlappung übertragen ließ'}
            . Das ist ein Ausgangspunkt und kein fertiges Bild: sie steht erst im Projekt,
            wenn du sie bestätigst oder auf der Fläche etwas anfasst.
            {proposal.overflow.length > 0 && (
              <>
                {' '}
                {proposal.overflow.length} Widget(s) haben auf {spec.label} keinen Platz
                gefunden und bleiben ohne Platzierung.
              </>
            )}
          </p>
          <span class="hv-hmi-canvas__noteTools">
            <button type="button" class="hv-btn hv-btn--primary" onClick={onAcceptProposal}>
              Übernehmen
            </button>
            <button type="button" class="hv-btn" onClick={() => { store.proposal.value = null; }}>
              Verwerfen
            </button>
          </span>
        </div>
      )}

      {screen && !proposal && unplaced.length > 0 && (
        <p class="hv-hmi-canvas__note hv-hmi-canvas__note--proposal">
          {unplaced.length === widgetIds.length
            ? `Für ${spec.label} ist noch nichts festgelegt.`
            : `${unplaced.length} von ${widgetIds.length} Widgets haben für ${spec.label} noch keine Platzierung.`}{' '}
          Die gestrichelt umrandeten Widgets stehen in einer vorläufigen Anordnung; sie wird
          gespeichert, sobald du hier etwas verschiebst.
        </p>
      )}

      {/* Die Flaeche bleibt in JEDEM Fall im Baum und bekommt nie Kinder aus
          Preact: Gridstack haengt seine Zellen hier hinein, und ein Preact,
          das den Knoten austauscht, liesse eine fremde Instanz auf einem
          verwaisten Element zurueck. */}
      {/* The project's theme around the surface only, not around the tools
          above: those are editor chrome and keep the app's look. CSS
          variables reach into the Gridstack cells through the DOM. */}
      <ThemeScope appearance={project.appearance} class="hv-hmi-canvas__theme">
      <div
        ref={surfaceRef}
        class={surfaceClasses}
        {...hmiAppearanceAttributes(project.appearance)}
        style={{
          // Feste Ziele in ihrer echten Breite. Das ist keine Kosmetik: ein
          // Panel in voller Fensterbreite laesst jeden Knopf groesser
          // aussehen, als er auf dem Geraet ist, und die Handschuh-Grenze aus
          // dem HMI-Konzept waere am Bildschirm nicht mehr zu beurteilen.
          // `maxWidth` und nicht `width`, damit ein schmaler Bildschirm
          // massstaeblich verkleinert statt abzuschneiden.
          ...(spec.widthPx !== null ? { maxWidth: `${spec.widthPx}px` } : {}),
          ...(spec.rows !== null ? { height: `${spec.rows * spec.cellHeightPx}px` } : {}),
        }}
        onClick={onSurfaceClick}
        onDragOver={onDragOver}
        onDragLeave={() => setDropHover(false)}
        onDrop={onDrop}
      />
      </ThemeScope>
    </div>
  );
}

/**
 * Steht der Fokus in etwas, das Tastendruecke selbst braucht?
 *
 * Bewusst grosszuegig: lieber eine Verknuepfung, die einmal nicht greift,
 * als ein Entf, das beim Tippen ein Widget loescht.
 */
function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
